import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Switch,
  Alert,
  Card,
  Box,
  Badge,
  Anchor,
  Tabs,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconAlertCircle, IconWorld, IconSettings, IconPhoto } from '@tabler/icons-react';
import {
  APP_STATE_TYPE_ID,
  APP_WEB_DOMAIN_LABELS,
} from '@/lib/constants/app';
import type { PfileRow } from '@lib2/model/pfile/PfileTypes';
import { useCreateAppWeb, useUpdateAppWeb } from '../useAppMutations';
import { AppUtils } from '../AppUtils';
import { appWebFormSchema, type AppWebFormSchemaData } from '../schemas/AppWebForm.schema';
import { AppWebCreateDialog } from './AppWebCreateDialog';
import { AppImagesTab } from './AppImagesTab';
import type { AppCfgsDto, AppWebFormData, AppCfgWebCreateReq } from '@/lib/types/App';

interface AppWebTabProps {
  appCfgId: number;
  app: AppCfgsDto;
  isAppActive: boolean;
  webAllowed: boolean;
  pfiles: PfileRow[];
  cId: number;
}

export function AppWebTab({ appCfgId, app, isAppActive, webAllowed, pfiles, cId }: AppWebTabProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const createWebMutation = useCreateAppWeb(appCfgId);
  const updateWebMutation = useUpdateAppWeb(appCfgId);

  const web = app.web;

  // ── Web Create ──

  const handleWebCreate = useCallback(
    (customName: string, sharedDomainId: number) => {
      const req: AppCfgWebCreateReq = {
        appCfgId: app.appCfg.core.appCfgId,
        web: {
          core: {
            appStateTypeId: APP_STATE_TYPE_ID.Active,
            customName,
            sharedDomainId: sharedDomainId as 1 | 2 | 3,
            customDomain: null,
          },
          style: {
            styleIsDarkMode: false,
            styleBGColorHex: null,
            styleTextColorHex: null,
          },
          seo: {
            allowIndex: true,
          },
        },
      };
      createWebMutation.mutate(req, {
        onSuccess: () => {
          setCreateDialogOpen(false);
          notifications.show({
            title: 'Web App Enabled',
            message: 'Web platform has been enabled.',
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
        },
      });
    },
    [app.appCfg.core.appCfgId, createWebMutation],
  );

  // ── No web platform yet — show enable card ──

  if (!web) {
    if (!webAllowed) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          Your current plan does not include access to the Web platform. Contact support to
          upgrade.
        </Alert>
      );
    }

    return (
      <>
        <Card withBorder p="xl" radius="md">
          <Stack align="center" gap="md">
            <IconWorld size={48} color="var(--mantine-color-blue-5)" />
            <Text fw={600} size="lg">
              Web App
            </Text>
            <Text c="dimmed" ta="center" maw={400}>
              Enable a web app to give your audience a branded streaming experience. Choose a
              custom subdomain and configure your site settings.
            </Text>
            <Button onClick={() => setCreateDialogOpen(true)} disabled={!isAppActive}>
              Enable Web App
            </Button>
            {!isAppActive && (
              <Text size="sm" c="orange">
                Activate the app first to enable web platform.
              </Text>
            )}
          </Stack>
        </Card>

        <AppWebCreateDialog
          opened={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSubmit={handleWebCreate}
          isPending={createWebMutation.isPending}
        />
      </>
    );
  }

  // ── Web platform exists — show settings with nested sub-tabs ──

  return (
    <Tabs defaultValue="settings" variant="pills">
      <Tabs.List mb="md">
        <Tabs.Tab value="settings" leftSection={<IconSettings size={14} />}>
          Settings
        </Tabs.Tab>
        <Tabs.Tab value="images" leftSection={<IconPhoto size={14} />}>
          Images
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="settings" keepMounted>
        <WebSettingsForm
          appCfgId={appCfgId}
          web={web}
          isAppActive={isAppActive}
          updateMutation={updateWebMutation}
        />
      </Tabs.Panel>

      <Tabs.Panel value="images">
        <AppImagesTab
          appCfgId={appCfgId}
          cId={cId}
          pfiles={pfiles}
        />
      </Tabs.Panel>
    </Tabs>
  );
}

// ── Internal: Web Settings Form ──

interface WebSettingsFormProps {
  appCfgId: number;
  web: NonNullable<AppCfgsDto['web']>;
  isAppActive: boolean;
  updateMutation: ReturnType<typeof useUpdateAppWeb>;
}

function WebSettingsForm({ appCfgId, web, isAppActive, updateMutation }: WebSettingsFormProps) {
  const domainLabel = APP_WEB_DOMAIN_LABELS[web.core.sharedDomainId] ?? 'unknown';
  const fullDomain = `${web.core.customName}.${domainLabel}`;
  const isWebActive = web.core.appStateTypeId === APP_STATE_TYPE_ID.Active;
  const disabled = !isAppActive;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<AppWebFormSchemaData>({
    resolver: zodResolver(appWebFormSchema),
    defaultValues: AppUtils.transformWebToFormData(web),
  });

  // Sync form when web data changes (after mutation success)
  useEffect(() => {
    reset(AppUtils.transformWebToFormData(web));
  }, [web, reset]);

  // Custom Domain, BG Color, Text Color hidden — not ready for customer configuration yet
  // const { field: customDomainField } = useController({ control, name: 'customDomain' });
  const { field: darkModeField } = useController({ control, name: 'styleIsDarkMode' });
  // const { field: bgColorField } = useController({ control, name: 'styleBGColorHex' });
  // const { field: textColorField } = useController({ control, name: 'styleTextColorHex' });
  const { field: allowIndexField } = useController({ control, name: 'allowIndex' });
  const { field: webStateField } = useController({ control, name: 'appStateTypeId' });

  const handleSave = handleSubmit((data: AppWebFormSchemaData) => {
    const req = AppUtils.transformWebFormToUpdateReq(data as AppWebFormData, web);
    updateMutation.mutate(req, {
      onSuccess: () => {
        notifications.show({
          title: 'Saved',
          message: 'Web settings updated successfully.',
          color: 'green',
        });
      },
      onError: (err) => {
        const msg =
          err.message?.includes('409') || err.message?.includes('conflict')
            ? 'Web settings were modified in another session. Please refresh and try again.'
            : err.message;
        notifications.show({ title: 'Error', message: msg, color: 'red' });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    reset(AppUtils.transformWebToFormData(web));
  }, [web, reset]);

  return (
    <form onSubmit={handleSave}>
      {/* Sticky save bar */}
      <Box
        pos="sticky"
        top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
        bg="white"
        py="sm"
        style={{
          zIndex: 10,
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        }}
      >
        <Group justify="space-between">
          {isDirty ? (
            <Text size="sm" c="orange" fw={500}>
              Unsaved changes
            </Text>
          ) : (
            <div />
          )}
          <Group gap="sm">
            <Button variant="default" onClick={handleDiscard} disabled={!isDirty}>
              Discard
            </Button>
            <Button
              type="submit"
              disabled={!isDirty || disabled}
              loading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </Group>
        </Group>
      </Box>

      {disabled && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" mt="md">
          Web settings are read-only while the parent app is inactive. Activate the app to make
          changes.
        </Alert>
      )}

      <Stack gap="lg" mt="md">
        {/* Domain (clickable) */}
        <Group gap="xs">
          <Text size="sm" fw={500}>
            Domain:
          </Text>
          <Anchor href={`https://${fullDomain}`} target="_blank" rel="noopener noreferrer" underline="never">
            <Badge variant="light" color="blue" size="lg" style={{ cursor: 'pointer' }}>
              {fullDomain}
            </Badge>
          </Anchor>
          {web.core.customDomain && (
            <Anchor href={`https://${web.core.customDomain}`} target="_blank" rel="noopener noreferrer" underline="never">
              <Badge variant="outline" color="gray" size="lg" style={{ cursor: 'pointer' }}>
                {web.core.customDomain}
              </Badge>
            </Anchor>
          )}
        </Group>

        {/* Status */}
        <Switch
          label="Web App Active"
          description={isWebActive ? 'Web app is publicly accessible' : 'Web app is not accessible'}
          checked={webStateField.value === APP_STATE_TYPE_ID.Active}
          onChange={(e) =>
            webStateField.onChange(
              e.currentTarget.checked ? APP_STATE_TYPE_ID.Active : APP_STATE_TYPE_ID.Inactive,
            )
          }
          disabled={disabled}
        />

        {/* Custom Domain — hidden, not ready for customer configuration yet
        <TextInput
          label="Custom Domain"
          placeholder="www.example.com"
          description="Optional custom domain for your web app"
          value={customDomainField.value}
          onChange={customDomainField.onChange}
          onBlur={customDomainField.onBlur}
          maxLength={APP_CONSTRAINTS.CUSTOM_DOMAIN_MAX}
          disabled={disabled}
        />
        */}

        {/* Appearance */}
        <Text fw={600} size="md">
          Appearance
        </Text>

        <Switch
          label="Dark Mode"
          description="Use dark theme for the web app"
          checked={darkModeField.value}
          onChange={(e) => darkModeField.onChange(e.currentTarget.checked)}
          disabled={disabled}
        />

        {/* Background Color and Text Color — hidden, not ready for customer configuration yet
        <Group grow>
          <ColorInput
            label="Background Color"
            placeholder="#000000"
            value={bgColorField.value}
            onChange={bgColorField.onChange}
            disabled={disabled}
          />
          <ColorInput
            label="Text Color"
            placeholder="#ffffff"
            value={textColorField.value}
            onChange={textColorField.onChange}
            disabled={disabled}
          />
        </Group>
        */}

        {/* SEO */}
        <Text fw={600} size="md">
          SEO
        </Text>

        <Switch
          label="Allow Search Engine Indexing"
          description="When enabled, search engines can index your web app"
          checked={allowIndexField.value}
          onChange={(e) => allowIndexField.onChange(e.currentTarget.checked)}
          disabled={disabled}
        />
      </Stack>
    </form>
  );
}
